// StartupPlugins.tsx
import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Stripe } from '@capacitor-community/stripe';

import { getUsesBleReader } from 'src/utils/Services/DB_Utils';

import useUserDataStore from 'src/store/user';
import { useSystemStore } from 'src/store/SystemStore';
import { DeviceType } from 'src/store/SystemStore/types';
import { STRIPE_PUBLISHABLE_KEY } from 'src/config-global';

import PrinterTroubleshootDialog from 'src/components/printer/PrinterTroubleshootDialog';

import { PrinterPlugin } from './PrinterPlugin';

type StartupPluginsProps = {
  children: React.ReactNode;
};

export function StartupPlugins({ children }: StartupPluginsProps) {
  if (Capacitor.isPluginAvailable('Stripe')) {
    Stripe.initialize({
      publishableKey: STRIPE_PUBLISHABLE_KEY,
    });
  }
  const { setCurrentDeviceType, setUsesBleReader } = useSystemStore();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      setCurrentDeviceType(DeviceType.MOBILE);
    }

    // Load BLE reader setting from database
    const loadBleReaderSetting = async () => {
      try {
        const bleReaderConfig = await getUsesBleReader();
        if (bleReaderConfig?.data?.usesBleReader !== undefined) {
          setUsesBleReader(bleReaderConfig.data.usesBleReader);
        }
      } catch (error) {
        console.error('Failed to load BLE reader setting:', error);
      }
    };

    loadBleReaderSetting();
  }, [setCurrentDeviceType, setUsesBleReader]);

  // Set up Sentry user context tracking
  useEffect(() => {
    const unsubscribeUser = useUserDataStore.subscribe((state) => {
      if (state.profile.currentUser && state.business.id) {
        Sentry.setUser({
          id: state.profile.currentUser.userId || 'unknown',
          email: state.profile.email || undefined,
        });

        Sentry.setTags({
          business_id: state.business.id,
          business_name: state.business.name,
          device_location_id: state.currentDevice.businessLocationId || 'unknown',
          offline_mode: state.business.offlineModeEnabled,
        });

      } else {
        Sentry.setUser(null);
      }
    });

    const unsubscribeSystem = useSystemStore.subscribe((state) => {
      Sentry.setTags({
        device_type: state.currentDeviceType,
        platform: Capacitor.getPlatform(),
        is_native: Capacitor.isNativePlatform(),
        app_version: state.appVersion.current,
        uses_ble_reader: state.usesBleReader,
      });
    });

    return () => {
      unsubscribeUser();
      unsubscribeSystem();
    };
  }, []);

  const { currentDeviceType } = useSystemStore();
  const onThWeb = currentDeviceType === DeviceType.DESKTOP;
  return (
    <>
      {!onThWeb && <PrinterPlugin />}
      {!onThWeb && <PrinterTroubleshootDialog />}
      {children}
    </>
  );
}
